/**
 * Response bodies of the admin ticketed-events endpoints that had no shared
 * contract: the envelopes around entities already declared in
 * `schemas/entities`, and the few small bodies of their own.
 *
 * The web dashboard parses each response with these. The entity schemas mirror
 * the backend DTOs (`services/ticketed-events/shared/types.ts`).
 */

import { z } from 'zod'

import { TicketTypePublicSchema } from '../../schemas/entities/ticket-type'
import {
  TicketedEventPublicSchema,
  TicketedEventSchema,
  TicketedEventWaiverRequirementPublicSchema
} from '../../schemas/entities/ticketed-event'
import { TicketedEventCollectionSchema } from '../../schemas/entities/ticketed-event-collection'
import { TicketedEventCustomQuestionPublicSchema } from '../../schemas/entities/ticketed-event-custom-question'
import { TicketedEventFaqPublicSchema } from '../../schemas/entities/ticketed-event-faq'
import { TicketedEventFormCompletionTimingSchema } from '../../schemas/entities/ticketed-event-form-requirement'
import { TicketedEventOrderSummarySchema } from '../../schemas/entities/ticketed-event-order'
import { CheckInLinkSchema } from './check-in-links'

const PageMetaSchema = z.object({
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int()
})

/** GET /v1/ticketed-events — one page of the active school's events. */
export const ListTicketedEventsResponseSchema = z.object({
  data: z.array(TicketedEventSchema),
  meta: PageMetaSchema,
  // Slug of the active school, so the list can build public event URLs. Null if unresolved.
  schoolSlug: z.string().nullable()
})
export type ListTicketedEventsResponse = z.infer<typeof ListTicketedEventsResponseSchema>

/** GET, PATCH /v1/ticketed-events/:id, POST /v1/ticketed-events and POST .../:id/status. */
export const TicketedEventResponseSchema = z.object({ data: TicketedEventSchema })
export type TicketedEventResponse = z.infer<typeof TicketedEventResponseSchema>

/**
 * GET /v1/ticketed-events/:id/preview — the public event DTO the by-slug page
 * renders, read for a draft. A waiver also carries when it is due.
 */
export const TicketedEventPreviewSchema = TicketedEventPublicSchema.extend({
  ticketTypes: z.array(TicketTypePublicSchema).optional(),
  customQuestions: z.array(TicketedEventCustomQuestionPublicSchema).optional(),
  faqs: z.array(TicketedEventFaqPublicSchema).optional(),
  waiverRequirements: z
    .array(
      TicketedEventWaiverRequirementPublicSchema.extend({ completionTiming: TicketedEventFormCompletionTimingSchema })
    )
    .optional(),
  requirePerAttendeeData: z.boolean().optional()
})
export const TicketedEventPreviewResponseSchema = z.object({ data: TicketedEventPreviewSchema })
export type TicketedEventPreviewResponse = z.infer<typeof TicketedEventPreviewResponseSchema>

/** GET /v1/ticketed-events/orders — the school-wide order list. */
export const ListSchoolOrdersResponseSchema = z.object({
  data: z.array(TicketedEventOrderSummarySchema),
  meta: PageMetaSchema
})
export type ListSchoolOrdersResponse = z.infer<typeof ListSchoolOrdersResponseSchema>

/** GET /v1/ticketed-events/:id/check-in-link and POST .../check-in-link/regenerate. */
export const CheckInLinkResponseSchema = z.object({ data: CheckInLinkSchema })
export type CheckInLinkResponse = z.infer<typeof CheckInLinkResponseSchema>

/** POST /v1/ticketed-events/:id/orders/:orderId/toggle-check-in. */
export const ToggleOrderCheckInResponseSchema = z.object({
  orderPublicId: z.string(),
  checkedInAt: z.string().nullable()
})
export type ToggleOrderCheckInResponse = z.infer<typeof ToggleOrderCheckInResponseSchema>

/** GET /v1/ticketed-events/collections/:id — the collection and the events filed into it. */
export const TicketedEventCollectionDetailSchema = z.object({
  collection: TicketedEventCollectionSchema,
  eventPublicIds: z.array(z.string())
})
export type TicketedEventCollectionDetail = z.infer<typeof TicketedEventCollectionDetailSchema>

/** DELETE /v1/ticketed-events/organizers/:id. */
export const DeleteTicketedEventOrganizerResponseSchema = z.object({ id: z.string() })
export type DeleteTicketedEventOrganizerResponse = z.infer<typeof DeleteTicketedEventOrganizerResponseSchema>
